import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { userPermssionsStore } from '../../store/permissions.store';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const clearPermissions = userPermssionsStore((state) => state.clearPermissions);
  const queryClient = useQueryClient();

  // Wipes both Zustand state AND React Query cache so the next
  // login never receives a previous user's stale permission data.
  const resetPermissions = () => {
    clearPermissions();
    queryClient.removeQueries({ queryKey: ['viewPermissionsList'] });
  };

  const isTokenExpired = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload?.exp) return true;
      return payload.exp * 1000 <= Date.now();
    } catch (error) {
      return true;
    }
  };

  useEffect(() => {
    const checkAuth = () => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      if (accessToken && refreshToken && !isTokenExpired(accessToken)) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        resetPermissions();
        setIsAuthenticated(false);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const intervalId = setInterval(() => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!accessToken || !refreshToken || isTokenExpired(accessToken)) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        resetPermissions();
        setIsAuthenticated(false);
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const login = async ({ accessToken, refreshToken }) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    setIsAuthenticated(true);
    return true;
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    resetPermissions();
    setIsAuthenticated(false);
  };

  const value = {
    isAuthenticated,
    loading,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
